import * as Yup from 'yup';
import { humanizeFieldKey } from './schemaToControls';
import type { RaterRule, RaterRulesByField, SchemaNode } from './types';

const isRequired = (node: SchemaNode, fieldKey: string): boolean =>
  (node.properties?.props?.required ?? []).includes(fieldKey);

const labelFor = (fieldKey: string, node: SchemaNode): string =>
  node.title ?? humanizeFieldKey(fieldKey);

// Empty inputs arrive as '' from the controls; treat as missing so `required`
// reports "<label> is required" instead of a type error.
const emptyToUndefined = (value: unknown, original: unknown) =>
  original === '' || original === null ? undefined : value;

function numberField(
  scalar: SchemaNode['properties'],
  rule: RaterRule | undefined,
  required: boolean,
  label: string
): Yup.NumberSchema {
  let schema = Yup.number()
    .label(label)
    .transform(emptyToUndefined)
    .typeError(`${label} must be a number`);
  const min = rule?.from ?? scalar?.value?.minimum;
  const max = rule?.to ?? scalar?.value?.maximum;
  if (min !== undefined) {
    schema = schema.min(Number(min));
  }
  if (max !== undefined) {
    schema = schema.max(Number(max));
  }
  if (rule?.options?.length) {
    schema = schema.oneOf(rule.options.map(Number));
  }
  return required ? schema.required() : schema;
}

function stringField(
  rule: RaterRule | undefined,
  required: boolean,
  label: string
): Yup.StringSchema {
  let schema = Yup.string().label(label);
  if (rule?.options?.length) {
    schema = schema.oneOf(rule.options.map(String));
  }
  return required ? schema.required() : schema;
}

function dateField(
  rule: RaterRule | undefined,
  required: boolean,
  label: string
): Yup.DateSchema {
  let schema = Yup.date()
    .label(label)
    .transform(emptyToUndefined)
    .typeError(`${label} must be a valid date`);
  if (rule?.fromDate) {
    schema = schema.min(new Date(rule.fromDate));
  }
  if (rule?.toDate) {
    schema = schema.max(new Date(rule.toDate));
  }
  return required ? schema.required() : schema;
}

function arrayField(
  array: NonNullable<NonNullable<SchemaNode['properties']>['values']>,
  rule: RaterRule | undefined,
  required: boolean,
  label: string
): Yup.Schema<unknown> {
  // `items` may be a single schema or a tuple ([rowSchema]); unwrap so each row's
  // own required fields are validated (otherwise empty rows pass).
  const item = Array.isArray(array.items) ? array.items[0] : array.items;
  const row = item ? buildValidationSchema(item, {}) : Yup.object();
  let schema = Yup.array().label(label).of(row);
  const max = rule?.listMaxSize ?? array.maxItems;
  if (max !== undefined) {
    schema = schema.max(max);
  }
  return required ? schema.required() : schema;
}

function fieldSchema(
  node: SchemaNode,
  rule: RaterRule | undefined,
  required: boolean,
  label: string
): Yup.Schema<unknown> {
  const array = node.properties?.values;
  if (array) {
    return arrayField(array, rule, required, label);
  }

  // Object group: validate its child fields as a nested object, using the group's
  // own props.required for per-field requiredness (rule-less, like array rows).
  const groupChildren = node.properties?.props?.properties;
  if (groupChildren && Object.keys(groupChildren).length > 0) {
    const schema = buildValidationSchema(node, {});
    return required ? schema.required() : schema;
  }

  const value = node.properties?.value;
  if (value?.type === 'number') {
    return numberField(node.properties, rule, required, label);
  }
  if (value?.type === 'boolean') {
    const schema = Yup.boolean().label(label);
    return required ? schema.required() : schema;
  }
  if (value?.type === 'string' && value.format === 'date') {
    return dateField(rule, required, label);
  }
  return stringField(rule, required, label);
}

// Builds a Yup object schema for an object node: schema-derived validation merged
// with the rater-rule overlay (rules win where present), joined by field name.
export function buildValidationSchema(
  node: SchemaNode,
  rules: RaterRulesByField
): Yup.ObjectSchema<object> {
  const props = node.properties?.props?.properties ?? {};
  const shape: Record<string, Yup.Schema<unknown>> = {};

  for (const [fieldKey, child] of Object.entries(props)) {
    shape[fieldKey] = fieldSchema(
      child,
      rules[fieldKey],
      isRequired(node, fieldKey),
      labelFor(fieldKey, child)
    );
  }

  return Yup.object().shape(shape);
}
